import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { cancelUpdateOrder, getOrderById, updateOrder, getInstallationByOrderId, updateInstall, resendInstallEmail } from '../../actions/order';
import { openDialog, closeDialog } from '../../actions/dialog';
import { storeWithExpiration, formatParamData } from '../../utils/common';
import {
  PROVIDER_ADMIN,
  PROVIDER_SALES_OPS,
  RECORD_LEVEL_TYPE_CONTACT,
  RECORD_LEVEL_TYPE_LEAD,
  RECORD_LEVEL_TYPE_COMPANY,
  RECORD_LEVEL_TYPE_ITEM,
  SUPER_ADMIN,
  VALID_EMAIL_REGEX,
} from '../../config/app_config';
import ViewOrder from './ViewOrder';

const FORM_ID = 'editOrderForm';

export class ViewOrderContainer extends React.Component {
  static propTypes = {
    cancelUpdateOrderAction: PropTypes.func.isRequired,
    closeDialogAction: PropTypes.func.isRequired,
    creditsRemaining: PropTypes.number,
    dialog: PropTypes.object.isRequired,
    dirty: PropTypes.bool.isRequired,
    getOrderByIdAction: PropTypes.func.isRequired,
    getInstallationByOrderIdAction: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    hasEditPerms: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isSuperAdmin: PropTypes.bool.isRequired,
    install: PropTypes.object,
    openDialogAction: PropTypes.func.isRequired,
    order: PropTypes.object,
    orderId: PropTypes.string.isRequired,
    pristine: PropTypes.bool.isRequired,
    skus: PropTypes.array,
    submitting: PropTypes.bool.isRequired,
    updateInstallAction: PropTypes.func.isRequired,
    updateOrderAction: PropTypes.func.isRequired,
    resendInstallEmailAction: PropTypes.func.isRequired,
    useCases: PropTypes.object,
  };

  static defaultProps = {
    creditsRemaining: null,
    install: undefined,
    order: undefined,
    skus: undefined,
    useCases: undefined,
  };

  componentWillMount = () => {
    const { orderId, getOrderByIdAction, getInstallationByOrderIdAction } = this.props;
    // TODO: Build an API endpoint to a new route for installiong table if SuperAdmin
    const token = storeWithExpiration.get('token');
    getInstallationByOrderIdAction(orderId, token);
    getOrderByIdAction(orderId, token);
  };

  stringToBoolean = (value) => {
     if (value && typeof value === "string") {
          if (value.toLowerCase() === "true") return true;
          if (value.toLowerCase() === "false") return false;
     }
     return value;
  }

  updateOrder = params => {
    const { updateInstallAction, updateOrderAction, order, install } = this.props;
    const token = storeWithExpiration.get('token');
    order.recordCredits = parseInt(params.credits, 10);
    order.numberOfSeats = params.seats;
    order.endDate = moment(params.endDate).format("YYYY-MM-DD[T00:00:00]");
    install.customConfiguration.csvDownloadEnabled = this.stringToBoolean(params.updatedCsvDownloadEnabled);
    install.customConfiguration.leadMatchEnabled = this.stringToBoolean(params.updatedLeadMatchEnabled);
    install.customConfiguration.contactMatchByAccount = this.stringToBoolean(params.updatedContactMatchByAccount);
    install.customConfiguration.preferEndUserCredentials = this.stringToBoolean(params.updatedPreferEndUserCredentials);
    updateInstallAction(install, 'Install', token);
    updateOrderAction(order, 'Order', token);
  };

  updateInstall = status => {
    const { updateInstallAction } = this.props;
    const token = storeWithExpiration.get('token');
    let { install } = this.props;
    install = install.merge({
      installStatus: status,
      crmAuthId: status === 'PENDING' ? null : install.crmAuthId
    })
    updateInstallAction(install, 'Status', token);
  };

  resendInstallEmail = params => {
    const { resendInstallEmailAction, install } = this.props;
    const token = storeWithExpiration.get('token');
    const email = params.emailAddress;
    resendInstallEmailAction(email, install, 'Install', token);
  };

  toggleIsBillable = () => {
    const { updateOrderAction, order } = this.props;
    const token = storeWithExpiration.get('token');
    order.isBillable = !order.isBillable;
    updateOrderAction(order, 'Billing', token);
  };

  render() {
    const {
      cancelUpdateOrderAction,
      closeDialogAction,
      creditsRemaining,
      dialog,
      dirty,
      handleSubmit,
      hasEditPerms,
      invalid,
      isSuperAdmin,
      install,
      openDialogAction,
      order,
      pristine,
      skus,
      submitting,
      useCases,
    } = this.props;

    return (
      <ViewOrder
        cancelUpdateOrderFunction={cancelUpdateOrderAction}
        closeDialogFunction={closeDialogAction}
        creditsRemaining={creditsRemaining}
        dialog={dialog}
        dirty={dirty}
        handleSubmit={handleSubmit}
        hasEditPerms={hasEditPerms}
        invalid={invalid}
        isSuperAdmin={isSuperAdmin}
        install={install}
        openDialogFunction={openDialogAction}
        order={order}
        pristine={pristine}
        skus={skus}
        submitting={submitting}
        toggleIsBillable={this.toggleIsBillable}
        updateOrderFunction={this.updateOrder}
        updateInstallFunction={this.updateInstall}
        resendInstallEmailFunction={this.resendInstallEmail}
        useCases={useCases}
      />
    );
  }
}

function mapStateToProps(state, ownProps) {
  const { orderId } = ownProps.params;
  const { dialog, orderList, progress, user } = state;
  const tempOrder = orderList.get('currentOrder');
  const order = tempOrder.get('order');
  const skus = tempOrder.get('listSKUs');
  const platformUseCases = tempOrder.get('platformUseCases');
  const savedSearches = tempOrder.get('savedSearches');
  const creditsRemaining = tempOrder.get('creditsRemaining');
  const initialCredits = order ? order.recordCredits : null;
  const creditsUsed = initialCredits !== null ? initialCredits - creditsRemaining : null;
  const initialSeats = order ? order.numberOfSeats : null;
  const initialEndDate = order ? order.endDate : null;
  const initialStartDate = order ? order.startDate : null;
  const initialEmailAddress = order ? order.person.email : null;
  const install = orderList.get('install');
  const isSuperAdmin = user.get('userTypeId') === SUPER_ADMIN;
  const useCases = {
    platform: '',
    platformCode: '',
    cases: '',
    listCriteria: '',
    selectionCriteria: null,
  };
  const userRole = user.get('userTypeId');
  const hasEditPerms = userRole === PROVIDER_ADMIN || userRole === PROVIDER_SALES_OPS;

  if (platformUseCases) {
    let platforms = platformUseCases.map(p => p.platform.description);
    platforms = Array.from(new Set(platforms));

    if (platforms) {
      platformUseCases.forEach(p => {
        if (useCases.platform === '' && useCases.cases === '') {
          useCases.platform = p.platform.description;
          useCases.platformCode = p.platform.code;
          useCases.cases = p.useCase.description;
        } else {
          useCases.cases += `, ${p.useCase.description}`;
        }
      });
    }
  }

  // Add list criteria to the usecase section
  useCases.listCriteria = savedSearches && savedSearches.length > 0 ? 'Partial File' : 'Full File';

  if (savedSearches && savedSearches.length > 0) {
    // Format the saved Searches
    useCases.savedSearches = [];
    for (let i = 0; i < savedSearches.length; i++) {
      const search = savedSearches[i].searchRequest;
      let recordLevel = '';
      let groups = [];
      if (search && search.criteria) {
        // Add tab title
        if (search.recordLevel === RECORD_LEVEL_TYPE_CONTACT) {
          recordLevel = 'Contacts';
        } else if (search.recordLevel === RECORD_LEVEL_TYPE_COMPANY) {
          recordLevel = 'Accounts';
        } else if (search.recordLevel === RECORD_LEVEL_TYPE_ITEM) {
          recordLevel = 'Items';
        } else if (search.recordLevel === RECORD_LEVEL_TYPE_LEAD) {
          recordLevel = 'Leads';
        }

        groups = search.criteria.map(group => {
          const formattedGroup = {};
          if (group.params) {
            formattedGroup.params = group.params.map(formatParamData);
          }
          if (group.parentParams) {
            formattedGroup.parentParams = group.parentParams.map(formatParamData);
          }

          // selection groups
          if (group.groups && group.groups.length > 0) {
            formattedGroup.groupParams = [];
            group.groups.forEach(g => {
                formattedGroup.groupParams.push(...g.params.map(formatParamData));
            });
          }
          if (group.parentGroups && group.parentGroups.length > 0) {
            formattedGroup.parentGroupParams = [];
            group.parentGroups.forEach(g => {
                formattedGroup.parentGroupParams.push(...g.params.map(formatParamData));
            });
          }

          return formattedGroup;
        });
      }

      useCases.savedSearches.push({ recordLevel, groups });
    }
  }

  const initialValues =
    initialCredits !== null && creditsUsed !== null && progress < 1
      ? { credits: initialCredits, creditsUsed }
      : {};
  if (initialSeats !== null && progress < 1) {
    initialValues.seats = initialSeats;
    initialValues.initialSeats = initialSeats;
  }
  if (initialEndDate !== null && progress < 1) {
    initialValues.endDate = moment(initialEndDate).format("YYYY-MM-DD");
    initialValues.initialEndDate = moment(initialEndDate).format("YYYY-MM-DD");
    initialValues.initialStartDate = moment(initialStartDate).format("YYYY-MM-DD");
  }
  if (initialEmailAddress!== null && progress < 1) {
    initialValues.emailAddress = initialEmailAddress;
  }
  if (install.customConfiguration !== null && progress < 1) {
    initialValues.updatedCsvDownloadEnabled = install.customConfiguration.csvDownloadEnabled;
    initialValues.updatedLeadMatchEnabled = install.customConfiguration.leadMatchEnabled;
    initialValues.updatedContactMatchByAccount = install.customConfiguration.contactMatchByAccount;
    initialValues.updatedPreferEndUserCredentials = install.customConfiguration.preferEndUserCredentials;
  }

  const enableReinitialize = true;

  return {
    creditsRemaining,
    dialog,
    enableReinitialize,
    hasEditPerms,
    initialValues,
    isSuperAdmin,
    install,
    order,
    orderId,
    skus,
    useCases,
  };
}

const isValidDate = value => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid();
};

const validate = values => {
  const errors = {};
  if (!values.credits || values.credits === '') {
    errors.credits = 'Required';
  } else if (parseInt(values.credits, 10) < values.creditsUsed) {
    errors.credits = 'credits remaining cannot be less than 0';
  }
  if (!values.seats || values.seats === '') {
    errors.seats = 'Required';
  } else if (parseInt(values.seats,10) < 1) {
    errors.seats = 'number of seats can not be less than 1';
  }
  if (!isValidDate(values.endDate)) {
    errors.endDate = 'invalid date';
  } else if (moment(values.endDate).format() < moment().format()) {
    errors.endDate = 'end date can not be less than today';
  } else if (moment(values.endDate).format() < moment(values.initialStartDate).format()) {
    errors.endDate = 'end date can not be less than start date';
  }
  if (!values.emailAddress || values.emailAddress.trim() === '') {
    errors.emailAddress = 'Required';
  } else if (!VALID_EMAIL_REGEX.test(values.emailAddress)) {
    errors.emailAddress = 'Invalid email address';
  }

  return errors;
};

const ViewOrderContainerRF = reduxForm({
  form: FORM_ID,
  validate,
})(ViewOrderContainer);

export default connect(mapStateToProps, {
  cancelUpdateOrderAction: cancelUpdateOrder,
  getOrderByIdAction: getOrderById,
  getInstallationByOrderIdAction: getInstallationByOrderId,
  openDialogAction: openDialog,
  closeDialogAction: closeDialog,
  updateInstallAction: updateInstall,
  updateOrderAction: updateOrder,
  resendInstallEmailAction: resendInstallEmail,
})(ViewOrderContainerRF);
